import { useEffect, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useToast } from '../../components/Toast';
import Modal from '../../components/Modal';
import Icon from '../../components/Icon';
import { Badge, Banner, PageLoading } from '../../components/ui';
import { OUTCOME_LABEL, clockTime, duration, flagInfo, useCompanyTimeZone } from './visitMeta';
import type { Visit } from '../../types';

/**
 * One visit, in full.
 *
 * Opening this is also the only thing in the product that spends a
 * reverse-geocoding call — the API resolves the address here and stores
 * it, so the second person to open the same visit costs nothing.
 */
export default function VisitDetail({
  visitId, canReview, onClose, onReviewed,
}: {
  visitId: string;
  canReview: boolean;
  onClose: () => void;
  onReviewed?: () => void;
}) {
  const toast = useToast();
  const tz = useCompanyTimeZone();
  const [visit, setVisit] = useState<Visit | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<Visit>(`/visits/${visitId}`);
        if (!cancelled) setVisit(data);
      } catch (err) {
        if (!cancelled) {
          toast.error(err instanceof ApiClientError ? err.message : 'Could not load that visit.');
          onClose();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [visitId, toast, onClose]);

  async function review(accept: boolean) {
    setBusy(true);
    try {
      await api.post(`/visits/${visitId}/review`, { accept });
      toast.success(accept ? 'Marked as fine.' : 'Kept flagged for follow-up.');
      onReviewed?.();
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  }

  const title = visit?.customer?.name ?? visit?.siteLabel ?? 'Visit';

  return (
    <Modal
      title={title}
      subtitle={visit ? `${visit.user?.name ?? 'Unknown rep'} · ${visit.visitDate}` : undefined}
      wide
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          {canReview && visit?.isFlagged && !visit.reviewedAt ? (
            <>
              <button
                type="button"
                className="btn btn-danger-ghost"
                disabled={busy}
                onClick={() => void review(false)}
              >
                Keep flagged
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy}
                onClick={() => void review(true)}
              >
                This is fine
              </button>
            </>
          ) : null}
        </>
      )}
    >
      {loading || !visit ? <PageLoading rows={3} /> : (
        <div className="col gap-16">
          {visit.flags.length ? (
            <div className="col gap-8">
              {visit.flags.map((flag) => {
                const info = flagInfo(flag);
                return (
                  <Banner key={flag} kind={info.tone === 'danger' ? 'danger' : info.tone === 'warning' ? 'warning' : 'info'}>
                    <strong>{info.label}</strong>
                    {info.why ? <div style={{ marginTop: 2 }}>{info.why}</div> : null}
                  </Banner>
                );
              })}
            </div>
          ) : null}

          <div className="kv">
            <span>Outcome</span>
            <span>
              {visit.outcome ? (
                <Badge tone={visit.isProductive ? 'success' : 'neutral'}>
                  {OUTCOME_LABEL[visit.outcome]}
                </Badge>
              ) : <span className="faint">Still in progress</span>}
            </span>

            <span>On site</span>
            <span>
              {clockTime(visit.startedAt, tz)} → {clockTime(visit.endedAt, tz)}
              {' '}
              <span className="faint">({duration(visit.durationSeconds)})</span>
            </span>

            {visit.customer ? (
              <>
                <span>Area</span>
                <span>{visit.customer.route ?? <span className="faint">—</span>}</span>
              </>
            ) : null}

            <span>Where</span>
            <span>
              {visit.address ?? (
                visit.start
                  ? <span className="mono small">
                      {visit.start.latitude.toFixed(5)}, {visit.start.longitude.toFixed(5)}
                    </span>
                  : <span className="faint">No location recorded</span>
              )}
              {visit.distanceM != null ? (
                <div className="small faint" style={{ marginTop: 2 }}>
                  {visit.distanceM < 1000
                    ? `${visit.distanceM} m from the stored pin`
                    : `${(visit.distanceM / 1000).toFixed(1)} km from the stored pin`}
                </div>
              ) : null}
            </span>

            {visit.wasPlanned || visit.flags.includes('OFF_PLAN') ? (
              <>
                <span>Journey plan</span>
                <span>
                  {visit.wasPlanned
                    ? <Badge tone="brand">On plan</Badge>
                    : <Badge tone="neutral">Off plan</Badge>}
                </span>
              </>
            ) : null}

            {visit.isOfflineSync ? (
              <>
                <span>Sync</span>
                <span className="small faint">
                  Recorded offline and sent when the phone found signal.
                </span>
              </>
            ) : null}

            {visit.note ? (
              <>
                <span>Note</span>
                <span>{visit.note}</span>
              </>
            ) : null}
          </div>

          {visit.answers?.length ? (
            <div className="card">
              <div className="card-head"><h3>What the rep filled in</h3></div>
              <div className="card-pad">
                <div className="kv">
                  {visit.answers.map((a) => (
                    <span key={a.key} style={{ display: 'contents' }}>
                      <span>{a.label}</span>
                      <span>{a.display || <span className="faint">—</span>}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          {visit.selfieUrl ? (
            <div className="card">
              <div className="card-head"><h3>Check-in selfie</h3></div>
              <div className="card-pad">
                <a href={visit.selfieUrl} target="_blank" rel="noreferrer">
                  <img
                    src={visit.selfieUrl}
                    alt="Check-in selfie"
                    loading="lazy"
                    style={{
                      width: 140, height: 140, objectFit: 'cover',
                      borderRadius: 10, border: '1px solid var(--border)',
                    }}
                  />
                </a>
              </div>
            </div>
          ) : null}

          {visit.photos?.length ? (
            <div className="card">
              <div className="card-head"><h3>Photos</h3></div>
              <div className="card-pad">
                <div className="row gap-8" style={{ flexWrap: 'wrap' }}>
                  {visit.photos.map((p, i) => (
                    <a key={i} href={p.url} target="_blank" rel="noreferrer">
                      <img
                        src={p.url}
                        alt={`Visit photo ${i + 1}`}
                        loading="lazy"
                        style={{
                          width: 120, height: 120, objectFit: 'cover',
                          borderRadius: 10, border: '1px solid var(--border)',
                        }}
                      />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          {visit.signature ? (
            <div className="card">
              <div className="card-head"><h3>Sign-off</h3></div>
              <div className="card-pad">
                {visit.signature.skipped ? (
                  <div className="row gap-8 faint">
                    <Icon name="info" size={16} />
                    <span>
                      Nobody signed
                      {visit.signature.reason ? ` — ${visit.signature.reason}` : '.'}
                    </span>
                  </div>
                ) : (
                  <div className="col gap-8">
                    <div className="small faint">Signed by {visit.signature.name ?? 'unnamed'}</div>
                    {visit.signature.url ? (
                      <a href={visit.signature.url} target="_blank" rel="noreferrer">
                        <img
                          src={visit.signature.url}
                          alt="Signature"
                          loading="lazy"
                          style={{
                            maxWidth: 320, width: '100%', background: '#fff',
                            borderRadius: 10, border: '1px solid var(--border)',
                          }}
                        />
                      </a>
                    ) : (
                      <div className="signature-frame">
                        <span className="small faint">Signature stored with the visit.</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
